import { useQueries, useQuery } from '@tanstack/react-query'
import { UserRole } from '@kfolks/types'
import { isAdmin, roleAtLeast } from '@/auth'
import { useAuth } from '@/hooks/useAuth'
import { dashboardApi } from '../api/dashboard.api'
import { dashboardKeys } from './query-keys'

const STALE_60S = 60_000

/**
 * Static spec for the 9 admin cross-module tiles. Shape matches the
 * `useQueries({ queries: [...] })` input one-to-one, so the helper hook
 * stays a thin loop. Order is preserved across the destructure in the
 * caller.
 */
const ADMIN_COUNT_QUERY_SPECS: Array<{ key: string; endpoint: string }> = [
	{ key: 'employees', endpoint: '/employees' },
	{ key: 'contractors', endpoint: '/employees/contractors' },
	{ key: 'entra-directory', endpoint: '/entra-directory' },
	{ key: 'clients', endpoint: '/clients' },
	{ key: 'projects', endpoint: '/projects' },
	{ key: 'providers', endpoint: '/providers' },
	{ key: 'idm-requests', endpoint: '/employees/idm-requests' },
	{ key: 'idm-acc-mappings', endpoint: '/employees/idm-acc-mappings' },
	{ key: 'permissions', endpoint: '/permissions' },
]

/**
 * Run the 9 cross-module count tiles in parallel. Each gates via
 * `enabled: admin` so non-admin USERs skip the whole batch.
 */
function useCrossModuleCountQueries(admin: boolean) {
	return useQueries({
		queries: ADMIN_COUNT_QUERY_SPECS.map((spec) => ({
			queryKey: dashboardKeys.count(spec.key),
			queryFn: () => dashboardApi.getCount(spec.endpoint),
			enabled: admin,
			staleTime: STALE_60S,
		})),
	})
}

/** Five viewer-side queries (stats/my, open-requests, recent-activity, closed-last-30d, request-types-catalog). */
function useViewerQueries() {
	return useQueries({
		queries: [
			{
				queryKey: dashboardKeys.statsMy(),
				queryFn: dashboardApi.getStatsMy,
				staleTime: STALE_60S,
			},
			{
				queryKey: dashboardKeys.openRequests(),
				queryFn: dashboardApi.getOpenRequests,
				staleTime: STALE_60S,
			},
			{
				queryKey: dashboardKeys.recentActivity(),
				queryFn: dashboardApi.getRecentActivity,
				staleTime: STALE_60S,
			},
			{
				queryKey: dashboardKeys.closedLast30d(),
				queryFn: dashboardApi.getClosedLast30d,
				staleTime: STALE_60S,
			},
			{
				// `MyOpenRequestsWidget` joins `requestTypeId → name` against this
				// catalog to render the human title beside the SR number. Stays
				// fresh enough at 60s to pick up renamed types between sessions.
				queryKey: dashboardKeys.requestTypesCatalog(),
				queryFn: dashboardApi.getRequestTypesCatalog,
				staleTime: STALE_60S,
			},
		],
	})
}

/** Pending-approvals query gated by the visibility predicate. */
function usePendingApprovalsQuery(visible: boolean) {
	return useQuery({
		queryKey: dashboardKeys.pendingApprovals(),
		queryFn: dashboardApi.getPendingApprovals,
		enabled: visible,
		staleTime: STALE_60S,
	})
}

/**
 * Admin Snapshot (RC) gated on `admin`; IDM-pending-confirmation count (non-RC)
 * gated on `superAdmin` so ADMIN users don't fire /idm-requests fetches that
 * would 403 under ADR-0009 (admin is RC-scoped).
 */
function useAdminCoreQueries(admin: boolean, superAdmin: boolean) {
	const adminStatsQuery = useQuery({
		queryKey: dashboardKeys.adminStats(),
		queryFn: dashboardApi.getAdminStats,
		enabled: admin,
		staleTime: STALE_60S,
	})
	const pendingIdmConfirmationQuery = useQuery({
		queryKey: dashboardKeys.pendingIdmConfirmations(),
		queryFn: dashboardApi.getPendingIdmConfirmations,
		enabled: superAdmin,
		staleTime: STALE_60S,
	})
	return { adminStatsQuery, pendingIdmConfirmationQuery }
}

/**
 * Composes the dashboard's data layer for non-VIEWER roles.
 *
 * Per spec (`request-center-dashboard`):
 *   - Dashboard composition is frontend-only — no backend orchestration endpoint.
 *   - TanStack Query caching is applied (60s staleTime + stale-while-revalidate).
 *   - Per-widget failure does not break the page.
 *
 * Two predicates gate widgets internally:
 *   - `pendingApprovalsVisible` — admin OR pendingApprovals > 0 OR hadEverPendingApproval
 *     (drives Pending Approvals + Upcoming SLA visibility).
 *   - `isAdminViewer` — admin shortcut (drives Admin Snapshot, cross-module
 *     counts, Pending Actions callout, Quick Links).
 *
 * Admin queries split by scope (ADR-0009):
 *   - `adminStatsQuery` (RC Admin Snapshot) gates on `admin` — ADMIN + SUPER_ADMIN.
 *   - `pendingIdmConfirmationQuery` and the 9 cross-module counts (all non-RC)
 *     gate on `superAdmin` so ADMIN users never trigger the platform-grade
 *     fetches that would 403 anyway.
 * Non-admin USERs make ZERO admin fetches; ADMIN users make only the RC fetch.
 */
export function useDashboard() {
	const { user } = useAuth()
	const admin = isAdmin(user?.role)
	const superAdmin = roleAtLeast(user?.role, UserRole.SUPER_ADMIN)

	const [statsMyQuery, openRequestsQuery, recentActivityQuery, closedLast30dQuery, requestTypesCatalogQuery] =
		useViewerQueries()

	// Visibility predicate: admin OR pendingApprovals > 0 OR hadEverPendingApproval.
	const stats = statsMyQuery.data
	const pendingApprovalsVisible =
		admin || (stats !== undefined && (stats.pendingApprovals > 0 || stats.hadEverPendingApproval))

	const pendingApprovalsQuery = usePendingApprovalsQuery(pendingApprovalsVisible)
	// adminStatsQuery is the RC Admin Snapshot — gated on admin (ADMIN + SUPER_ADMIN).
	// pendingIdmConfirmationQuery feeds the non-RC PendingActionsCallout widget —
	// gated on superAdmin so ADMIN no longer triggers /idm-requests fetches it
	// can't see anyway (ADR-0009).
	const { adminStatsQuery, pendingIdmConfirmationQuery } = useAdminCoreQueries(admin, superAdmin)

	// Cross-module counts feed the non-RC CrossModuleCountsSection widget.
	// Gated on superAdmin so ADMIN doesn't fan out fetches to /employees,
	// /clients, /idm-* etc. that would 403 (ADR-0009).
	const [
		employeesCountQuery,
		contractorsCountQuery,
		entraDirectoryCountQuery,
		clientsCountQuery,
		projectsCountQuery,
		providersCountQuery,
		idmRequestsCountQuery,
		accMappingsCountQuery,
		permissionsCountQuery,
	] = useCrossModuleCountQueries(superAdmin)

	return {
		statsMyQuery,
		openRequestsQuery,
		recentActivityQuery,
		closedLast30dQuery,
		requestTypesCatalogQuery,
		pendingApprovalsQuery,
		/** True iff Pending Approvals + Upcoming SLA widgets should render. */
		pendingApprovalsVisible,

		/** True iff the caller is ADMIN or SUPER_ADMIN. */
		isAdminViewer: admin,

		/** True iff the caller is SUPER_ADMIN (used to gate non-RC affordances). */
		isSuperAdminViewer: superAdmin,

		// Admin overlay queries (only fire when `admin === true`).
		adminStatsQuery,
		pendingIdmConfirmationQuery,
		employeesCountQuery,
		contractorsCountQuery,
		entraDirectoryCountQuery,
		clientsCountQuery,
		projectsCountQuery,
		providersCountQuery,
		idmRequestsCountQuery,
		accMappingsCountQuery,
		permissionsCountQuery,
	}
}
